import { LayoutSizeContext } from 'App';
import PageIndex from 'components/UI/PageIndex';
import {
  GROUP_HISTORY_PAGE_SIZE,
  GROUP_HISTORY_START_MS,
} from 'features/Integrations/features/ConnectedDeviceGroups/features/GroupSettingsModal/utils/groupSettingsModalUtils';
import useDeviceSeriesData from 'features/Integrations/hooks/useDeviceSeriesData';
import { useContext, useState } from 'react';
import { Button, Card, Table } from 'react-bootstrap';
import { FaChevronLeft } from 'react-icons/fa';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { displayStatusOptions } from 'utils/devices/icon.utils';

const DeviceHistory = ({
  device,
  selectedDevice,
  widget = false,
  size = null,
  setShowHistory = () => {},
}) => {
  const [pageIndex, setPageIndex] = useState(1);
  const itemAmount = 10;
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const layout = useContext(LayoutSizeContext);

  const deviceDetails = connectedDevices[selectedDevice];

  // The page load brings one newest row per device; the history this card lists
  // is asked for here, for this one device, a page at a time. The window starts at
  // the epoch, the same as every other history surface: a command device writes a
  // row only when a person commands it, so a bounded window is legitimately empty
  // for an idle one. GROUP_HISTORY_PAGE_SIZE is what bounds the read.
  const {
    isFetching: historyLoading,
    hasMore: historyHasMore,
    loadMore: loadMoreHistory,
    error: historyError,
  } = useDeviceSeriesData({
    ids: deviceDetails?.id ? [deviceDetails.id] : [],
    startTime: GROUP_HISTORY_START_MS,
    limit: GROUP_HISTORY_PAGE_SIZE,
  });

  // The Audio column shows which file was selected for an activation. Only a
  // siren has one, so the column is absent — not empty — for every other
  // device type. `audio_name` is a snapshot taken at command time, served
  // from RI_Device_Command_History (migration 002 on the backend).
  const isSiren = deviceDetails?.device_type === 'Technology Bridge_Siren/Giant Voice';

  const historyRows =
    deviceDetails?.data?.slice((pageIndex - 1) * itemAmount, pageIndex * itemAmount) ?? [];

  const totalHistoryItems = deviceDetails?.data?.length ?? 0;
  const maxPages = Math.max(1, Math.ceil(totalHistoryItems / itemAmount));

  return (
    <Card
      className={`shadow-sm ${widget ? 'h-100' : 'mt-3'} border-0 text-white`}
      style={{
        backgroundColor: widget ? 'var(--color-primary)' : 'var(--color-primary-shadow)',
        height: widget
          ? size === '2x1'
            ? layout.widgetHeight - 90
            : layout.widgetHeight * 2 - 80
          : window.innerHeight / 3 + 50,
        maxHeight: widget ? '100%' : '50%',
      }}
    >
      <Card.Body className="d-flex flex-column h-100">
        <div className="d-flex justify-content-between align-items-center mb-3">
          {widget ? (
            <Button variant="outline-light" size="sm" onClick={() => setShowHistory(false)}>
              <FaChevronLeft />
            </Button>
          ) : (
            <div style={{ width: 25 }} />
          )}
          <div className="text-center">
            <Card.Title className="mb-1">Device History</Card.Title>
            <div className="small">
              {(deviceDetails?.device_type !== 'Manual Road Closure'
                ? deviceDetails?.static_id + ' '
                : '') + deviceDetails?.alias}
              ({deviceDetails?.node_id})
            </div>
          </div>
          <div style={{ width: 25 }} />
        </div>

        <div className="flex-grow-1 overflow-auto">
          {historyRows.length > 0 ? (
            <Table responsive striped hover size="sm" variant="dark" className="align-middle">
              <thead>
                <tr>
                  <th className="text-nowrap">Date/Time</th>
                  <th>Status</th>
                  {isSiren && <th>Audio</th>}
                  <th>Source</th>
                  <th>Comment</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((data, index) => (
                  <tr key={`${data.ts}-${index}`}>
                    <td>{new Date(data.ts).toLocaleString()}</td>
                    <td>
                      {data.status
                        ? displayStatusOptions(device)[1]
                        : displayStatusOptions(device)[0]}
                    </td>
                    {isSiren && (
                      <td className="text-break">
                        {data?.audio_name || <span className="text-muted">—</span>}
                      </td>
                    )}
                    <td>{data?.source ?? '-'}</td>
                    <td className="text-break">
                      {data.comments || <span className="text-muted fst-italic">No info</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <div
              className={`d-flex h-100 align-items-center justify-content-center text-center ${
                historyError ? 'text-danger' : 'text-muted'
              }`}
            >
              {/* A failed fetch is not an empty history — saying "No Data Available"
                  would report a device as never activated when the request failed. */}
              {historyError
                ? historyError.message || 'The history could not be loaded.'
                : historyLoading
                  ? 'Loading history…'
                  : 'No Data Available'}
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-between gap-2">
          <PageIndex pageIndex={pageIndex} setPageIndex={setPageIndex} maxPages={maxPages} />
          {(historyHasMore || historyLoading || historyError) && (
            <Button
              variant="outline-light"
              size="sm"
              disabled={historyLoading}
              onClick={loadMoreHistory}
            >
              {historyLoading ? 'Loading…' : historyError ? 'Retry' : 'Load more'}
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default DeviceHistory;
